import {
  constructionForGauges,
  type StringConstruction,
  type ApprovedPresetConstruction,
} from "@/lib/configurator/preset-sets";

// ─────────────────────────────────────────────────────────
// "Customize this set" deep links.
//
// The shop used to link to `/configure?i=<instrument>` and nothing else, so a
// button promising to customise *this set* dropped the line, the string count,
// every gauge, and the colour the customer was looking at.
//
// This builds the full configurator URL — the same slug-based parameter shape
// useConfiguratorUrlSync restores from — and refuses to build one at all when
// the specification is incomplete, so the caller can fall back to an honest
// generic label instead of promising an exact set it cannot reproduce.
// ─────────────────────────────────────────────────────────

/** Instrument category ("electric") → configurator instrument slug. */
const CATEGORY_TO_INSTRUMENT_SLUG: Record<string, string> = {
  electric: "electric-guitar",
  acoustic: "acoustic-guitar",
  bass: "bass-guitar",
  classical: "classical-guitar",
  ukulele: "ukulele",
};

/** Product lines the configurator can actually build. */
const CONFIGURABLE_LINES = new Set([
  "aurora-one",
  "ultra-sheer",
  "ultra-flex",
  "prime",
  "stainless-steel",
]);

export function colorSlugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export interface ConfigureSpec {
  category: string;
  /** Product line slug, e.g. "aurora-one". */
  lineSlug: string;
  /** Full ordered gauge sequence. String count is its length. */
  gauges: string[];
  /** Selected colour's display name, if the customer has picked one. */
  colorName?: string | null;
  verifiedPreset?: ApprovedPresetConstruction;
}

export interface ConfigureLink {
  href: string;
  /**
   * True when every part of the set carried across: line, instrument, string
   * count, the complete gauge sequence, and construction. False means the link
   * only opens the builder at the right instrument, and the CTA must say so.
   */
  exact: boolean;
}

/**
 * Build a configurator link for a specific set.
 *
 * Returns `exact: false` (and a link carrying whatever we could establish)
 * whenever the full specification is not reproducible — an unknown line, a
 * gauge sequence Aurora doesn't wind, or a missing gauge list.
 */
export function buildConfigureLink(spec: ConfigureSpec): ConfigureLink {
  const instrumentSlug = CATEGORY_TO_INSTRUMENT_SLUG[spec.category];
  if (!instrumentSlug) return { href: "/configure", exact: false };

  const params = new URLSearchParams();
  params.set("i", instrumentSlug);

  const lineKnown = CONFIGURABLE_LINES.has(spec.lineSlug);
  if (lineKnown) params.set("l", spec.lineSlug);

  const gauges = spec.gauges.filter((g) => g.trim().length > 0);
  const construction: StringConstruction[] | null =
    gauges.length > 0 ? constructionForGauges(spec.category, gauges, spec.verifiedPreset ? { lineSlug: spec.lineSlug, preset: spec.verifiedPreset } : undefined) : null;

  // Without verified per-string construction we will not fabricate a build.
  // Carry the instrument (and line, if known) and let the customer choose.
  if (!construction) {
    return { href: `/configure?${params.toString()}`, exact: false };
  }

  params.set("sc", String(gauges.length));
  params.set("p", spec.verifiedPreset!.id);

  gauges.forEach((gauge, index) => {
    const value = normalizeGauge(gauge);
    const winding = spec.verifiedPreset!.strings[index].windingType;
    const parts = [value, winding];
    params.set(`s${index + 1}`, parts.join(","));
  });

  if (spec.colorName) params.set("c", colorSlugify(spec.colorName));

  return { href: `/configure?${params.toString()}`, exact: lineKnown };
}

/** "10" / "0.010" / ".010" → "0.010", the form the configurator stores. */
function normalizeGauge(value: string): string {
  const n = parseFloat(value.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return value;
  const inches = n >= 1 ? n / 1000 : n;
  const decimals = Math.round(inches * 1000) === inches * 1000 ? 3 : 4;
  return inches.toFixed(decimals);
}

/** Convenience wrapper for anything already carrying parsed product meta. */
export function buildConfigureHrefFromProduct(meta: {
  category: string;
  productLineSlug: string;
  individualGauges: string;
}, colorName?: string | null): ConfigureLink {
  return buildConfigureLink({
    category: meta.category,
    lineSlug: meta.productLineSlug,
    gauges: meta.individualGauges.split("|").map((g) => g.trim()),
    colorName,
  });
}

/** CTA wording that matches what the link actually does. */
export function configureLabel(link: ConfigureLink): string {
  return link.exact ? "Customize this set" : "Open the configurator";
}
